/**
 * Card primitives — the shared "cardbox" look.
 *
 * `Card` is a token-themed surface (`bg-surface`, `rounded-token-lg`,
 * `border-border`, `shadow-elevation-1`) with an optional accent header strip
 * and a footer slot. When interactive it lifts on hover (the modern 3D card
 * feel: a translate + scale + deeper shadow + a faint top-light sheen).
 * `GridCard` is a `Card` that lifts by default, tuned to live inside a
 * `CardGrid` — a responsive `grid gap-4 sm:grid-cols-2 lg:grid-cols-3`.
 *
 * Pass `to` to render the whole card as a React Router `<Link>` (the card gets
 * the `group` class so children can use `group-hover:*`); otherwise it renders
 * a `<div>` and forwards native props such as `onClick` / `aria-*`.
 *
 * Rich affordances (all opt-in, all token-themed / dark-mode correct):
 *   - `ribbon` prop → a diagonal corner ribbon (status/flag), or `CardRibbon`.
 *   - `CardThumb`  → a gradient thumbnail band (icon / emoji / <img>).
 *   - `CardMeter`  → a labelled score / progress bar with an accessible
 *                    `role="progressbar"`.
 */

import { type HTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useT } from '@/i18n';

export type AccentTone = 'primary' | 'accent' | 'success' | 'warning' | 'danger';

/** Saturated gradient — the top accent strip and the ribbon. */
const ACCENT_STRIP: Record<AccentTone, string> = {
  primary: 'from-primary to-accent',
  accent: 'from-accent to-primary',
  success: 'from-success to-success/70',
  warning: 'from-warning to-warning/70',
  danger: 'from-danger to-danger/70',
};

/** Soft tinted gradient — used as a card thumbnail background band. */
const THUMB_TINT: Record<AccentTone, string> = {
  primary: 'from-primary/20 via-accent/10 to-transparent',
  accent: 'from-accent/20 via-primary/10 to-transparent',
  success: 'from-success/20 via-success/5 to-transparent',
  warning: 'from-warning/25 via-warning/5 to-transparent',
  danger: 'from-danger/20 via-danger/5 to-transparent',
};

// `isolate` gives every card its own stacking context so the decorative
// hover-sheen overlay (rendered at `-z-10`) sits above the card background but
// below the content, without leaking behind ancestor surfaces.
const BASE =
  'relative isolate rounded-token-lg border border-border bg-surface text-foreground shadow-elevation-1';
// Modern tactile "3D" affordance: a subtle lift (translate + micro-scale) +
// a layered, accent-tinted glow shadow + accent border on hover/focus. Motion
// is disabled for `prefers-reduced-motion` and the keyboard focus ring mirrors
// the pointer hover so it's fully accessible. The glow uses the live accent
// token (`--color-primary`) so it re-tints with the selected theme.
const LIFT =
  'transition duration-200 ease-out will-change-transform ' +
  'hover:-translate-y-1 hover:scale-[1.01] hover:border-primary/40 ' +
  'hover:shadow-[0_22px_48px_-16px_rgb(var(--color-primary)/0.32),0_8px_18px_-10px_rgb(2_6_23/0.28)] ' +
  'focus-visible:-translate-y-1 focus-visible:scale-[1.01] focus-visible:border-primary/40 ' +
  'focus-visible:shadow-[0_22px_48px_-16px_rgb(var(--color-primary)/0.32),0_8px_18px_-10px_rgb(2_6_23/0.28)] ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg ' +
  'motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:hover:scale-100 ' +
  'motion-reduce:focus-visible:translate-y-0 motion-reduce:focus-visible:scale-100';

/**
 * The bare hover/focus "3D lift" affordance (no `cursor-pointer`) for grid tiles
 * that are NOT rendered through `Card` — e.g. semantic `<li>` media tiles that
 * still want to match the modern-kit lift. Prefer `Card`/`GridCard` when the
 * tile is a real link/button; use this only when the element must stay a
 * non-`Card` node.
 */
export const CARD_LIFT = LIFT;

export interface RibbonSpec {
  label: ReactNode;
  tone?: AccentTone;
}

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Optional coloured gradient strip along the top edge. */
  accent?: AccentTone;
  /** Diagonal corner ribbon (top-right) — a compact status/flag affordance. */
  ribbon?: RibbonSpec;
  /** Content rendered in a bordered header band above the body. */
  header?: ReactNode;
  /** Content rendered in a bordered footer band below the body. */
  footer?: ReactNode;
  /** Enables the hover lift + pointer affordance. Auto-on when `to`/`onClick` set. */
  interactive?: boolean;
  /** Wrap the body in default padding (`p-4`). Defaults to true. */
  padded?: boolean;
  /** Render the whole card as a `<Link to=…>`. */
  to?: string;
  children?: ReactNode;
}

export function Card({
  accent,
  ribbon,
  header,
  footer,
  interactive,
  padded = true,
  to,
  className = '',
  children,
  ...rest
}: CardProps) {
  const clickable = interactive ?? (to != null || rest.onClick != null);
  const classes = [
    BASE,
    'flex flex-col overflow-hidden',
    clickable ? `${LIFT} group cursor-pointer` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const body = (
    <>
      {/* Decorative hairline along the very top edge — a subtle "lit" bevel
          that reads as depth in dark mode (invisible on white surfaces). */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-px bg-gradient-to-r from-transparent via-white/50 to-transparent dark:via-white/10"
      />
      {/* Accent-tinted sheen that fades in on hover for interactive cards.
          Sits below the content (`-z-10`) inside the card's stacking context. */}
      {clickable && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10 rounded-[inherit] bg-gradient-to-b from-primary/[0.08] via-transparent to-transparent opacity-0 transition-opacity duration-300 ease-out group-hover:opacity-100 motion-reduce:transition-none"
        />
      )}
      {accent && (
        <div
          aria-hidden="true"
          className={`h-1.5 w-full shrink-0 bg-gradient-to-r ${ACCENT_STRIP[accent]}`}
        />
      )}
      {ribbon && <CardRibbon label={ribbon.label} tone={ribbon.tone} />}
      {header != null && (
        <div className="border-b border-border bg-surface-2/40 px-4 py-3">{header}</div>
      )}
      <div className={padded ? 'flex flex-1 flex-col p-4' : 'flex flex-1 flex-col'}>{children}</div>
      {footer != null && (
        <div className="border-t border-border bg-surface-2/50 px-4 py-3">{footer}</div>
      )}
    </>
  );

  if (to != null) {
    return (
      <Link to={to} className={classes} {...(rest as Record<string, unknown>)}>
        {body}
      </Link>
    );
  }
  return (
    <div className={classes} {...rest}>
      {body}
    </div>
  );
}

/** A `Card` that lifts on hover by default — the grid tile variant. */
export function GridCard({ interactive = true, ...props }: CardProps) {
  return <Card interactive={interactive} {...props} />;
}

// ---------------------------------------------------------------------------
// Rich affordances
// ---------------------------------------------------------------------------

export interface CardRibbonProps {
  label: ReactNode;
  tone?: AccentTone;
}

/**
 * Diagonal corner ribbon anchored to the top-right of a `Card`
 * (the card is `relative` + `overflow-hidden`, so the ribbon tails clip
 * cleanly). Decorative styling on real text — announced by screen readers.
 */
export function CardRibbon({ label, tone = 'primary' }: CardRibbonProps) {
  return (
    <span
      className={`pointer-events-none absolute -right-9 top-3.5 z-10 w-32 rotate-45 bg-gradient-to-r py-0.5 text-center text-[10px] font-semibold uppercase tracking-wide text-white shadow-elevation-1 ${ACCENT_STRIP[tone]}`}
    >
      {label}
    </span>
  );
}

export interface CardThumbProps {
  /** Tint of the fallback gradient band. */
  tone?: AccentTone;
  /** Override the gradient classes entirely (e.g. a platform-branded band). */
  gradient?: string;
  /** Band height utility (default `h-24`). */
  height?: string;
  className?: string;
  /** Icon, emoji, or <img> shown centered in the band. */
  children?: ReactNode;
}

/**
 * Gradient thumbnail band for the top of a card — a lightweight preview
 * affordance when there is no real image. Decorative by default.
 */
export function CardThumb({
  tone = 'primary',
  gradient,
  height = 'h-24',
  className = '',
  children,
}: CardThumbProps) {
  return (
    <div
      aria-hidden={children == null ? 'true' : undefined}
      className={`relative isolate flex ${height} shrink-0 items-center justify-center overflow-hidden bg-gradient-to-br ${
        gradient ?? THUMB_TINT[tone]
      } ${className}`.trim()}
    >
      {/* Soft top-left light source for a premium, 3D depth. Sits behind the
          children (`-z-10`) so full-bleed <img> content stays unaffected. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(120%_90%_at_25%_0%,rgb(255_255_255/0.28),transparent_55%)] dark:bg-[radial-gradient(120%_90%_at_25%_0%,rgb(255_255_255/0.1),transparent_55%)]"
      />
      {children}
    </div>
  );
}

export interface CardMeterProps {
  /** Current value (clamped to `[0, max]`). */
  value: number;
  /** Full-scale value (default `100`). */
  max?: number;
  label?: ReactNode;
  tone?: AccentTone;
  /** Show the percentage on the right of the label row. */
  showValue?: boolean;
  className?: string;
}

/**
 * Labelled score / progress bar — an accessible `role="progressbar"` with a
 * tone-coloured gradient fill. Theme-aware (track uses `bg-surface-2`).
 */
export function CardMeter({
  value,
  max = 100,
  label,
  tone = 'primary',
  showValue = false,
  className = '',
}: CardMeterProps) {
  const { t } = useT();
  const safeMax = max <= 0 ? 1 : max;
  const clamped = Math.max(0, Math.min(value, safeMax));
  const pct = Math.round((clamped / safeMax) * 100);
  return (
    <div className={className}>
      {(label != null || showValue) && (
        <div className="mb-1 flex items-center justify-between gap-2 text-xs text-muted">
          {label != null ? <span className="min-w-0 truncate">{label}</span> : <span />}
          {showValue && <span className="shrink-0 font-medium tabular-nums text-foreground">{pct}%</span>}
        </div>
      )}
      <div
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={safeMax}
        aria-label={typeof label === 'string' ? label : t('shell.ui.card.progress')}
        className="h-2 w-full overflow-hidden rounded-full bg-surface-2"
      >
        <div
          className={`h-full rounded-full bg-gradient-to-r ${ACCENT_STRIP[tone]} transition-[width] duration-500 ease-out motion-reduce:transition-none`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export interface CardGridProps {
  className?: string;
  children: ReactNode;
}

/**
 * Responsive card grid: 1 col (mobile) → 2 (sm) → 3 (lg) with a consistent gap.
 * `auto-rows-fr` keeps every tile in a row the same height so rich cardboxes
 * with uneven content still line up.
 */
export function CardGrid({ className = '', children }: CardGridProps) {
  return (
    <div
      className={`grid auto-rows-fr grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 ${className}`.trim()}
    >
      {children}
    </div>
  );
}
